import React, { ReactNode } from 'react';
import { Button, Dropdown, Menu, Table } from 'antd';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import cx from 'classnames';

import { ScrollPosition } from '../../../slices/utils';
import { TableSort } from '../../../utils/dataTable/data';
import { ExtendedColumnProps } from '../../../utils/dataTable/column';
import { IAccountDetails, IUser } from '../../../slices/users/user';
import { ReactComponent as SxEllipsis } from '../../../assets/sightx-icons/svg/icon-menu.svg';
import { ISettingsUser } from '../../settings/users/utils';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { EntityHandlers } from './dataTable.plgexternal';

import styles from './lightDataTable.module.scss';

interface IProps<T extends IDataTableEntity> {
  columns: ExtendedColumnProps<T>[];
  data: T[];
  loading: boolean;
  withActiveToggle: boolean;
  onToggleActiveState?: (entityToEdit: T) => void;
  sort: TableSort;
  loggedInUser: IUser | null;
  initialScroll?: ScrollPosition;
  onDeactivateUser: (userId: string, accountId: string) => void;
  toggleEditEntityHandler?: (isOpen: boolean, entityToEdit: T, handler: EntityHandlers | null) => void;
}

interface IDataTableEntity {
  id: string | number;
  isActive?: boolean;
  status?: string;
  account?: IAccountDetails;
  roles?: Array<string>;
}

const actionsColumnWidth = 48;

export class LightDataTable<T extends IDataTableEntity> extends React.Component<IProps<T>> {

  componentDidUpdate(): void {
    this.restoreScrollHeight();
  }

  private restoreScrollHeight(): void {
    const { initialScroll } = this.props;
    const tableContainer = document.getElementsByClassName('ant-table-content');
    if ((!initialScroll?.x && !initialScroll?.y) || !tableContainer.length) {
      return;
    }
    tableContainer[0].scrollTo(initialScroll.x, initialScroll.y);
  }

  private renderActionsColumn(value: ISettingsUser, _record: T): ReactNode {
    if (!value.displayActionMenu) {
      return null;
    }

    return (
      <div className={styles.actionsContainer}>
        {this.renderMenuActions(value, _record)}
      </div>
    );
  }

  private deactivateUser(userId: string, accountId: string) {
    this.props.onDeactivateUser(userId, accountId);
  }

  private renderMenuActions(value: ISettingsUser, _record: T): ReactNode {
    const iconIU = <Icon
      component={SxEllipsis}
      className={styles.editIcon}
    />;

    const menu = (<Menu selectable={false} className={styles.nonCollapsingMenu}>
      <Menu.Item
        key={`menu-item-${value.id}-1`}
        className={cx(styles.customMenuStyle, {
          [styles[Features.PlgExternal3]]: isFeatureEnabled(Features.PlgExternal3)
        })}
        onClick={this.deactivateUser.bind(this, value.id, value.accountId)}
      >
        <div>
          Deactivate
        </div>
      </Menu.Item>
      {/* TODO: this functionality will be implemented in the future according to Alexa's word */}
      <Menu.Item key={`menu-item-${value.id}-2`} hidden className={styles.customMenuStyle}>
        <div>
          Transfer Account
        </div>
      </Menu.Item>
    </Menu>);

    return (
      <Dropdown
        overlay={menu}
        trigger={['click']}
        className={styles.editIconContainer}>
        {iconIU}
      </Dropdown>
    );
  }

  private renderColumnTitle(colDefinition: ExtendedColumnProps<T>): ReactNode {
    return <div className={cx(styles.titleContainer, {
      [styles.actionsColumnTitle]: colDefinition.isActionsColumn
    })}>
      <div className={styles.columnTitle}>{colDefinition.title}</div>
    </div>;
  }

  private renderAccessColumn(_value: unknown, record: T): ReactNode {
    const { toggleEditEntityHandler } = this.props;
    if (!toggleEditEntityHandler) {
      return null;
    }
    return <div className={styles.buttonContainer}>
      <Button
        className={styles.button}
        type='link'
        onClick={toggleEditEntityHandler.bind(this, true, record, EntityHandlers.AccessPermissions)}
      >
        Edit access
      </Button>
    </div>;
  }

  render(): ReactNode {
    const { columns = [], data = [], loading } = this.props;
    const columnsWithHandlers = columns.map(col => {
      let renderMethod = col.render;

      if (col.mapper) {
        renderMethod = col.mapper;
        if (col.render) {
          renderMethod = (value, ...args) => col.render?.(col.mapper?.(value), ...args);
        }
      }

      if (isFeatureEnabled(Features.SectionsPermissions) && col.key === 'access') {
        renderMethod = this.renderAccessColumn.bind(this);
      }

      if (col.isActionsColumn) {
        renderMethod = this.renderActionsColumn.bind(this);
      }

      return {
        ...col,
        title: this.renderColumnTitle.bind(this, col),
        render: renderMethod,
        sorter: undefined,
        width: col.isActionsColumn ? `${actionsColumnWidth}px` : undefined,
        className: cx(styles.dataTableCell, { [styles.rightActionCell]: col.isActionsColumn })
      };
    });

    if (loading) {
      return <div className={styles.loadingContainer}>
        <LoadingOutlined className={styles.loadingSpin} />
      </div>;
    }

    return <Table<T>
      columns={columnsWithHandlers}
      rowKey="id"
      dataSource={data}
      pagination={false}
      scroll={{ x: 'max-content' }}
      className={styles.dataTable}
    />;
  }
}
